
export type LegalKey = 'impressum' | 'datenschutz';
export type LegalLocale = 'de' | 'en';

export interface LegalSection {
  readonly heading?: string;
  readonly paragraphs: readonly string[];
}
export interface LegalDoc {
  readonly title: string;
  readonly updated: string;
  readonly sections: readonly LegalSection[];
}

const OWNER = 'Rokas Sileikis';
const SITE = 'rokass.org';

export const LEGAL: Record<LegalKey, Record<LegalLocale, LegalDoc>> = {
  impressum: {
    de: {
      title: 'Impressum',
      updated: 'Juli 2026',
      sections: [
        {
          heading: 'Verantwortlich für diese Website',
          paragraphs: [
            OWNER,
            'Zürich, Schweiz',
            `Website: ${SITE}`,
            'Kontakt: über das Kontaktformular auf dieser Website.',
            'Postadresse und weitere Kontaktangaben auf Anfrage.',
          ],
        },
        {
          heading: 'Haftungsausschluss',
          paragraphs: [
            'Der Autor übernimmt keinerlei Gewähr hinsichtlich der inhaltlichen Richtigkeit, Genauigkeit, Aktualität, Zuverlässigkeit und Vollständigkeit der Informationen.',
            'Haftungsansprüche gegen den Autor wegen Schäden materieller oder immaterieller Art, welche aus dem Zugriff oder der Nutzung bzw. Nichtnutzung der veröffentlichten Informationen, durch Missbrauch der Verbindung oder durch technische Störungen entstanden sind, werden ausgeschlossen.',
            'Alle Angebote sind unverbindlich. Der Autor behält es sich ausdrücklich vor, Teile der Seiten oder das gesamte Angebot ohne besondere Ankündigung zu verändern, zu ergänzen, zu löschen oder die Veröffentlichung zeitweise oder endgültig einzustellen.',
          ],
        },
        {
          heading: 'Haftung für Links',
          paragraphs: [
            'Verweise und Links auf Webseiten Dritter liegen ausserhalb unseres Verantwortungsbereichs. Es wird jegliche Verantwortung für solche Webseiten abgelehnt. Der Zugriff und die Nutzung solcher Webseiten erfolgen auf eigene Gefahr des jeweiligen Nutzers.',
          ],
        },
        {
          heading: 'Urheberrecht',
          paragraphs: [
            `Die Urheber- und alle anderen Rechte an Inhalten, Bildern, Fotos oder anderen Dateien auf dieser Website gehören ausschliesslich ${OWNER} oder den speziell genannten Rechteinhabern. Für die Reproduktion jeglicher Elemente ist die schriftliche Zustimmung der Urheberrechtsträger im Voraus einzuholen.`,
          ],
        },
      ],
    },
    en: {
      title: 'Legal notice',
      updated: 'July 2026',
      sections: [
        {
          heading: 'Responsible for this website',
          paragraphs: [
            OWNER,
            'Zürich, Switzerland',
            `Website: ${SITE}`,
            'Contact: via the contact form on this website.',
            'Postal address and further contact details available on request.',
          ],
        },
        {
          heading: 'Disclaimer',
          paragraphs: [
            'The author assumes no liability for the correctness, accuracy, timeliness, reliability or completeness of the information provided.',
            'Liability claims against the author for material or immaterial damage arising from access to, use or non-use of the published information, from misuse of the connection or from technical faults are excluded.',
            'All offers are non-binding. The author expressly reserves the right to change, supplement or delete parts of the pages or the entire content without separate announcement, or to cease publication temporarily or permanently.',
          ],
        },
        {
          heading: 'Liability for links',
          paragraphs: [
            'References and links to third-party websites lie outside our area of responsibility. Any responsibility for such websites is declined. Access to and use of such websites is at the user’s own risk.',
          ],
        },
        {
          heading: 'Copyright',
          paragraphs: [
            `The copyright and all other rights to content, images, photos and other files on this website belong exclusively to ${OWNER} or the specifically named rights holders. The written consent of the copyright holders must be obtained in advance for the reproduction of any element.`,
          ],
        },
      ],
    },
  },

  datenschutz: {
    de: {
      title: 'Datenschutzerklärung',
      updated: 'Juli 2026',
      sections: [
        {
          heading: 'Verantwortliche Stelle',
          paragraphs: [
            `Verantwortlich für die Bearbeitung von Personendaten auf dieser Website (${SITE}) ist ${OWNER}, Zürich, Schweiz.`,
            'Kontakt über das Kontaktformular auf dieser Website; weitere Kontaktangaben auf Anfrage.',
          ],
        },
        {
          heading: 'Allgemeines',
          paragraphs: [
            'Gestützt auf das schweizerische Datenschutzgesetz (revDSG) sowie – soweit anwendbar – die Datenschutz-Grundverordnung (DSGVO) der EU hat jede Person Anspruch auf Schutz ihrer Personendaten. Wir bearbeiten Personendaten nur im Rahmen der gesetzlichen Bestimmungen und so sparsam wie möglich.',
            'Personendaten sind alle Angaben, die sich auf eine bestimmte oder bestimmbare Person beziehen.',
          ],
        },
        {
          heading: 'Hosting und Server-Logfiles',
          paragraphs: [
            'Diese Website wird bei Cloudflare Pages (Cloudflare, Inc.) gehostet. Beim Aufruf der Website werden automatisch technische Daten (z. B. IP-Adresse, Datum und Uhrzeit des Zugriffs, Browsertyp, Betriebssystem, aufgerufene Seiten) in Server-Logfiles erfasst.',
            'Diese Daten dienen der sicheren und stabilen Bereitstellung der Website sowie der Abwehr von Missbrauch und werden nicht mit anderen Datenquellen zusammengeführt. Dabei können Daten auch im Ausland (u. a. USA) bearbeitet werden; der Anbieter stellt hierfür geeignete Garantien bereit.',
          ],
        },
        {
          heading: 'Kontaktformular',
          paragraphs: [
            'Wenn Sie das Kontaktformular nutzen, werden die von Ihnen eingegebenen Angaben (Name, E-Mail-Adresse und Nachricht) zur Bearbeitung Ihrer Anfrage und für allfällige Anschlussfragen bearbeitet. Diese Daten geben wir nicht ohne Ihre Einwilligung an Dritte weiter.',
            'Für den Versand dieser Nachrichten per E-Mail nutzen wir Resend (Resend, Inc.) als Dienstleister und Auftragsverarbeiter. Die Formularinhalte und die für die Zustellung erforderlichen technischen Daten werden dazu an Resend übermittelt.',
          ],
        },
        {
          heading: 'Cookies und lokale Speicherung',
          paragraphs: [
            'Diese Website verwendet keine Tracking- oder Werbe-Cookies. Es werden lediglich technisch notwendige Angaben (z. B. zur Sprach- und Darstellungsauswahl) lokal in Ihrem Browser gespeichert.',
            'Es findet kein Web-Tracking und keine Analyse Ihres individuellen Nutzungsverhaltens statt.',
          ],
        },
        {
          heading: 'Ihre Rechte',
          paragraphs: [
            'Sie haben jederzeit das Recht auf Auskunft über die zu Ihrer Person bearbeiteten Personendaten sowie auf Berichtigung, Löschung oder Einschränkung der Bearbeitung. Wenden Sie sich dazu über das Kontaktformular auf dieser Website an uns.',
          ],
        },
        {
          heading: 'Datensicherheit',
          paragraphs: ['Die Übertragung sämtlicher Daten erfolgt verschlüsselt über HTTPS (TLS).'],
        },
        {
          heading: 'Änderungen',
          paragraphs: [
            'Wir können diese Datenschutzerklärung jederzeit anpassen. Es gilt die jeweils aktuelle, auf dieser Website publizierte Fassung.',
          ],
        },
      ],
    },
    en: {
      title: 'Privacy policy',
      updated: 'July 2026',
      sections: [
        {
          heading: 'Controller',
          paragraphs: [
            `The party responsible for processing personal data on this website (${SITE}) is ${OWNER}, Zürich, Switzerland.`,
            'Contact via the contact form on this website; further contact details available on request.',
          ],
        },
        {
          heading: 'General',
          paragraphs: [
            'Based on the Swiss Data Protection Act (revDSG) and — where applicable — the EU General Data Protection Regulation (GDPR), everyone is entitled to the protection of their personal data. We process personal data only within the scope of the law and as sparingly as possible.',
            'Personal data is any information relating to an identified or identifiable person.',
          ],
        },
        {
          heading: 'Hosting and server log files',
          paragraphs: [
            'This website is hosted on Cloudflare Pages (Cloudflare, Inc.). When the website is accessed, technical data (e.g. IP address, date and time of access, browser type, operating system, pages requested) is automatically recorded in server log files.',
            'This data serves the secure and stable provision of the website and the prevention of misuse, and is not combined with other data sources. Data may also be processed abroad (including the USA); the provider offers appropriate safeguards for this.',
          ],
        },
        {
          heading: 'Contact form',
          paragraphs: [
            'When you use the contact form, the details you enter (name, email address and message) are processed to handle your enquiry and for any follow-up questions. We do not pass this data on to third parties without your consent.',
            'To deliver these messages by email we use Resend (Resend, Inc.) as our service provider and processor. The form content and the technical data required for delivery are transmitted to Resend for this purpose.',
          ],
        },
        {
          heading: 'Cookies and local storage',
          paragraphs: [
            'This website uses no tracking or advertising cookies. Only technically necessary information (e.g. language and display preferences) is stored locally in your browser.',
            'No web tracking or analysis of your individual usage behaviour takes place.',
          ],
        },
        {
          heading: 'Your rights',
          paragraphs: [
            'You have the right at any time to information about the personal data processed about you, as well as to its correction, deletion or restriction of processing. To exercise these rights, contact us via the contact form on this website.',
          ],
        },
        {
          heading: 'Data security',
          paragraphs: ['All data is transmitted encrypted via HTTPS (TLS).'],
        },
        {
          heading: 'Changes',
          paragraphs: [
            'We may amend this privacy policy at any time. The current version published on this website applies.',
          ],
        },
      ],
    },
  },
};
